import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Euro, Users, AlertCircle, TrendingUp } from '../../icons/wp-icons';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { zeiterfassungApi } from '../../api/zeiterfassungApi';

// Farben für das Pie-Chart
const COLORS = ['#057867', '#0ea5e9', '#8b5cf6', '#f59e0b', '#ef4444', '#6b7280'];

function ZeiterfassungWidget() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zeitraum, setZeitraum] = useState('monat');

  useEffect(() => {
    loadStats();
  }, [zeitraum]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await zeiterfassungApi.getStats(zeitraum);
      setStats(data);
    } catch (error) {
      console.error('Fehler beim Laden der Zeiterfassungs-Statistiken:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        Keine Daten verfügbar
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-4 overflow-auto">
      {/* Zeitraum-Auswahl */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-shrink-0">
        <select
          value={zeitraum}
          onChange={(e) => setZeitraum(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
        >
          <option value="woche">Diese Woche</option>
          <option value="monat">Dieser Monat</option>
          <option value="quartal">Dieses Quartal</option>
          <option value="jahr">Dieses Jahr</option>
        </select>

        {/* Nicht abgerechnet Badge */}
        {stats.nichtAbgerechnet && stats.nichtAbgerechnet.anzahl > 0 && (
          <button
            onClick={() => navigate('/kalender')}
            className="flex items-center justify-center gap-2 px-3 py-2 bg-yellow-100 text-yellow-700 rounded-lg text-sm hover:bg-yellow-200 transition w-full sm:w-auto"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{stats.nichtAbgerechnet.anzahl} nicht abgerechnet</span>
            <span className="font-medium">({stats.nichtAbgerechnet.kostenFormatiert})</span>
          </button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-shrink-0">
        <div className="bg-primary-light rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-primary" />
            <span className="text-sm text-gray-600">Stunden</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {stats.gesamtArbeitszeitStunden.toFixed(1)}
          </p>
          <p className="text-xs text-gray-500">{stats.anzahlEinsaetze} Einsätze</p>
        </div>

        <div className="bg-green-50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Euro className="w-5 h-5 text-green-600" />
            <span className="text-sm text-gray-600">Umsatz</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {stats.gesamtKosten.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}€
          </p>
          <p className="text-xs text-gray-500">{stats.gesamtKostenFormatiert}</p>
        </div>

        <div className="bg-blue-50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-gray-600">Ø pro Einsatz</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {stats.durchschnittProEinsatz}
          </p>
          <p className="text-xs text-gray-500">{stats.durchschnittProEinsatzMinuten} Min</p>
        </div>

        <div className="bg-purple-50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-purple-600" />
            <span className="text-sm text-gray-600">Kunden</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {stats.topKunden?.length || 0}
          </p>
          <p className="text-xs text-gray-500">mit Zeiterfassung</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">
        {/* Tätigkeiten Pie-Chart */}
        <div className="bg-gray-50 rounded-xl p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Tätigkeiten</h4>
          {stats.nachTaetigkeit && stats.nachTaetigkeit.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.nachTaetigkeit}
                    dataKey="minuten"
                    nameKey="bezeichnung"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    label={({ bezeichnung, prozent }) => `${bezeichnung.substring(0, 10)}${bezeichnung.length > 10 ? '...' : ''} (${prozent}%)`}
                    labelLine={false}
                  >
                    {stats.nachTaetigkeit.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name, props) => [
                      `${props.payload.arbeitszeitFormatiert} (${props.payload.kostenFormatiert})`,
                      name
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500 text-sm">
              Keine Tätigkeiten erfasst
            </div>
          )}
        </div>

        {/* Zeitverlauf Bar-Chart */}
        <div className="bg-gray-50 rounded-xl p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Zeitverlauf</h4>
          {stats.zeitverlauf && stats.zeitverlauf.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.zeitverlauf}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="datumFormatiert"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `${v.toFixed(1)}h`}
                  />
                  <Tooltip
                    formatter={(value, name) => [
                      `${value.toFixed(1)} Stunden`,
                      'Arbeitszeit'
                    ]}
                    labelFormatter={(label) => `Datum: ${label}`}
                  />
                  <Bar
                    dataKey="stunden"
                    fill="#057867"
                    radius={[4, 4, 0, 0]}
                    name="Stunden"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500 text-sm">
              Keine Zeitdaten vorhanden
            </div>
          )}
        </div>
      </div>

      {/* Top Kunden Tabelle */}
      {stats.topKunden && stats.topKunden.length > 0 && (
        <div className="bg-gray-50 rounded-xl p-4 flex-shrink-0">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Top 5 Kunden nach Arbeitszeit</h4>
          <div className="space-y-2">
            {stats.topKunden.map((kunde, index) => (
              <div
                key={kunde.id}
                className="flex items-center justify-between p-2 bg-white rounded-lg cursor-pointer hover:bg-gray-50 transition"
                onClick={() => navigate(`/kunden/${kunde.id}`)}
              >
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary-light text-primary text-sm font-medium flex items-center justify-center">
                    {index + 1}
                  </span>
                  <span className="font-medium text-sm">{kunde.name}</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">{kunde.arbeitszeitFormatiert}</span>
                  <span className="font-medium text-primary">{kunde.kostenFormatiert}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ZeiterfassungWidget;
